/**
 * The report builder's "Filters" panel — a category rail on the left and the
 * selected category's options on the right. Drives the legacy `subFilters` /
 * `subFiltersText` / `filterText` columns via `SubFilterSelection`.
 *
 * Extracted from ReportBuilderWizard so the panel can be rendered and tested
 * on its own, without the wizard's router and react-query dependencies.
 */
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@/components/ui';
import { HelpTip } from '@/components/reports/HelpTip';
import type {
  ReportSubFilterCatalog,
  ReportSubFilterGroupWithChoices,
} from '@/types/customReports.types';
import type { SubFilterSelection, SubFilterSelections } from '@/utils/legacyOrderParity';

const HISTORICAL_LABEL_SUFFIX = ' (Current)';

export function FilterPanel({
  catalog,
  selections,
  onChange,
  showApplyToAll,
}: {
  catalog: ReportSubFilterCatalog | undefined;
  selections: SubFilterSelections;
  onChange: (next: SubFilterSelections) => void;
  showApplyToAll: boolean;
}) {
  const groups = catalog?.groups ?? [];
  const [activeKey, setActiveKey] = useState<string | null>(groups[0]?.key ?? null);

  useEffect(() => {
    if (!activeKey || !groups.find((g) => g.key === activeKey)) {
      setActiveKey(groups[0]?.key ?? null);
    }
  }, [groups, activeKey]);

  if (!catalog) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        <Spinner size="sm" /> Loading filters…
      </div>
    );
  }

  const activeGroup = groups.find((g) => g.key === activeKey) ?? groups[0];

  const updateGroup = (key: string, patch: Partial<SubFilterSelection>) => {
    const current = selections[key] ?? { ids: [], labels: [] };
    const next: SubFilterSelection = {
      ids: patch.ids ?? current.ids,
      labels: patch.labels ?? current.labels,
      applyToAll: 'applyToAll' in patch ? patch.applyToAll : current.applyToAll,
    };
    const out: SubFilterSelections = { ...selections, [key]: next };
    if (next.ids.filter((id) => id !== '' && id !== '0').length === 0) {
      delete out[key];
    }
    onChange(out);
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-2 text-sm font-semibold text-slate-900">
        Filters
        <HelpTip text="Pick which staff, facilities, lead sources, etc. the report should consider. Matches the legacy Filters panel — choose a category on the left, then check the items on the right." />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3">
        <div className="max-h-72 overflow-y-auto border-b border-slate-200 sm:border-b-0 sm:border-r">
          {groups.map((group) => {
            const isActive = group.key === activeKey;
            const count = selections[group.key]?.ids.filter((id) => id !== '' && id !== '0').length ?? 0;
            const applyToAll = Boolean(selections[group.key]?.applyToAll);
            return (
              <button
                type="button"
                key={group.key}
                onClick={() => setActiveKey(group.key)}
                className={
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm ' +
                  (isActive ? 'bg-slate-100 font-semibold text-slate-900' : 'text-slate-700 hover:bg-slate-50')
                }
              >
                <span>{group.label}</span>
                {count > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1">
                    <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white">
                      {count}
                    </span>
                    {applyToAll && (
                      <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                        All
                      </span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="col-span-1 p-3 sm:col-span-2">
          {activeGroup ? (
            <FilterPanelGroupBody
              // Remount on category switch so each category opens with an empty
              // search box. Legacy re-applied the previous term across the
              // switch (admin_report_list.php:595), which could leave a new
              // category looking empty.
              key={activeGroup.key}
              group={activeGroup}
              selection={selections[activeGroup.key] ?? { ids: [], labels: [] }}
              showApplyToAll={showApplyToAll}
              onUpdate={(patch) => updateGroup(activeGroup.key, patch)}
            />
          ) : (
            <div className="text-sm text-slate-500">No filter categories available.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterPanelGroupBody({
  group,
  selection,
  showApplyToAll,
  onUpdate,
}: {
  group: ReportSubFilterGroupWithChoices;
  selection: SubFilterSelection;
  showApplyToAll: boolean;
  onUpdate: (patch: Partial<SubFilterSelection>) => void;
}) {
  if (group.type === 'people') {
    return (
      <PeopleFilterBody
        group={group}
        selection={selection}
        showApplyToAll={showApplyToAll}
        onUpdate={onUpdate}
      />
    );
  }
  if (group.type === 'choices') {
    return (
      <ChoiceFilterBody
        group={group}
        selection={selection}
        showApplyToAll={showApplyToAll}
        onUpdate={onUpdate}
      />
    );
  }
  if (group.type === 'zipRange') {
    return <ZipRangeFilterBody group={group} selection={selection} onUpdate={onUpdate} />;
  }
  if (group.type === 'gender') {
    return (
      <RadioFilterBody
        group={group}
        selection={selection}
        onUpdate={onUpdate}
        choices={[
          { id: '0', label: 'Male or Female' },
          { id: '1', label: 'Male' },
          { id: '2', label: 'Female' },
        ]}
        scalarLabel="Gender"
      />
    );
  }
  if (group.type === 'feesDiscounts') {
    return <FeesDiscountsFilterBody selection={selection} onUpdate={onUpdate} />;
  }
  if (group.type === 'procedureCount') {
    return <ProcedureCountFilterBody selection={selection} onUpdate={onUpdate} />;
  }
  return null;
}

/**
 * Substring search over a category's options. Restores the legacy Filters
 * search box (`#filterSearch` + `filterSearchFunc()` in
 * `html/html/admin/admin_report_list.php` 3720-3726), which narrowed the
 * visible rows client-side.
 */
function OptionSearchBox({
  value,
  onChange,
  matchCount,
  totalCount,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  matchCount: number;
  totalCount: number;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}…`}
          aria-label={`Search ${label}`}
          className="w-full rounded-md border border-slate-300 bg-white py-1 pl-7 pr-2 text-sm focus:border-slate-900 focus:outline-none"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400"
        >
          ⌕
        </span>
      </div>
      {value.trim() !== '' && (
        <span className="whitespace-nowrap text-xs text-slate-500">
          {matchCount} of {totalCount}
        </span>
      )}
    </div>
  );
}

/** Case-insensitive substring match over the whole label. */
function matchesSearch(label: string, term: string): boolean {
  return label.toLowerCase().includes(term.toLowerCase());
}

function PeopleFilterBody({
  group,
  selection,
  showApplyToAll,
  onUpdate,
}: {
  group: ReportSubFilterGroupWithChoices;
  selection: SubFilterSelection;
  showApplyToAll: boolean;
  onUpdate: (patch: Partial<SubFilterSelection>) => void;
}) {
  // Legacy admin_report_list.php 5416: each person dim has a Historical /
  // Current / Both selector. Negative IDs encode "Current" assignment;
  // labels get a " (Current)" suffix at line 1874 for the chart cache.
  const [mode, setMode] = useState<'H' | 'C' | 'B'>('B');
  const [search, setSearch] = useState('');
  const term = search.trim();

  const peopleByMode = group.choices;
  const visiblePeople = useMemo(
    () => (term === '' ? peopleByMode : peopleByMode.filter((p) => matchesSearch(p.label, term))),
    [peopleByMode, term],
  );
  const idsClean = selection.ids.filter((id) => id !== '' && id !== '0');

  const togglePerson = (person: { id: string; label: string }, asCurrent: boolean, checked: boolean) => {
    const id = asCurrent ? `-${person.id}` : person.id;
    const label = asCurrent ? person.label + HISTORICAL_LABEL_SUFFIX : person.label;
    if (checked) {
      // Remove any prior copy (could be opposite sign for the same person).
      const next = idsClean.filter((existing) => existing !== id && existing !== person.id && existing !== `-${person.id}`);
      const labels = selection.labels.filter((existingLabel) => {
        const stripped = existingLabel.replace(/ \(Current\)$/, '');
        return stripped !== person.label;
      });
      onUpdate({ ids: [...next, id], labels: [...labels, label] });
    } else {
      const next = idsClean.filter((existing) => existing !== id);
      const labels = selection.labels.filter((existingLabel) => existingLabel !== label);
      onUpdate({ ids: next, labels });
    }
  };

  /**
   * Add every visible person on one side of the roster. Only offered when the
   * Roster selector names a side: with "Both" showing two independent
   * checkboxes per row, "select all" has no single meaning.
   */
  const selectAllVisible = () => {
    const asCurrent = mode === 'C';
    const next = mergeSelection(
      { ids: idsClean, labels: selection.labels },
      visiblePeople.map((person) => ({
        id: asCurrent ? `-${person.id}` : person.id,
        label: asCurrent ? person.label + HISTORICAL_LABEL_SUFFIX : person.label,
      })),
    );
    onUpdate(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-900">{group.label}</div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-600">
            Roster:&nbsp;
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as 'H' | 'C' | 'B')}
              className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs"
            >
              <option value="B">Both</option>
              <option value="H">Historical</option>
              <option value="C">Current</option>
            </select>
          </label>
          {showApplyToAll && group.supportsApplyToAll && (
            <label className="flex items-center gap-1 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(selection.applyToAll)}
                onChange={(e) => onUpdate({ applyToAll: e.target.checked })}
              />
              Apply to All
            </label>
          )}
        </div>
      </div>
      {peopleByMode.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500">
          No {group.label.toLowerCase()} available for this account.
        </div>
      ) : (
        <>
          <OptionSearchBox
            value={search}
            onChange={setSearch}
            matchCount={visiblePeople.length}
            totalCount={peopleByMode.length}
            label={group.label}
          />
          <SelectionActions
            onSelectAll={mode === 'B' ? undefined : selectAllVisible}
            selectAllLabel={
              term === ''
                ? `Select all ${mode === 'C' ? 'Current' : 'Historical'}`
                : `Select ${visiblePeople.length} matching`
            }
            selectAllDisabled={visiblePeople.length === 0}
            onClear={() => onUpdate({ ids: [], labels: [] })}
            clearDisabled={idsClean.length === 0}
            selectedCount={idsClean.length}
          />
          <div className="max-h-56 overflow-y-auto rounded-md border border-slate-300 bg-white p-2">
            {visiblePeople.length === 0 ? (
              <div className="px-1 py-2 text-xs text-slate-500">No matches for “{term}”.</div>
            ) : (
              visiblePeople.map((person) => {
                const showHistorical = mode === 'H' || mode === 'B';
                const showCurrent = mode === 'C' || mode === 'B';
                const checkedH = idsClean.includes(person.id);
                const checkedC = idsClean.includes(`-${person.id}`);
                return (
                  <div key={person.id} className="flex items-center gap-3 py-0.5 text-sm">
                    <span className="flex-1 text-slate-700">{person.label}</span>
                    {showHistorical && (
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={checkedH}
                          onChange={(e) => togglePerson(person, false, e.target.checked)}
                        />
                        Historical
                      </label>
                    )}
                    {showCurrent && (
                      <label className="flex items-center gap-1 text-xs text-amber-700">
                        <input
                          type="checkbox"
                          checked={checkedC}
                          onChange={(e) => togglePerson(person, true, e.target.checked)}
                        />
                        Current
                      </label>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Merge choices into an existing selection, keeping `ids[i]` and `labels[i]`
 * paired — they are positional in `SubFilterSelection` and get encoded that way
 * into the legacy columns. Already-selected ids are left alone rather than
 * duplicated.
 */
function mergeSelection(
  current: { ids: string[]; labels: string[] },
  additions: Array<{ id: string; label: string }>,
): { ids: string[]; labels: string[] } {
  const ids = [...current.ids];
  const labels = [...current.labels];
  const present = new Set(ids);
  for (const addition of additions) {
    if (present.has(addition.id)) continue;
    present.add(addition.id);
    ids.push(addition.id);
    labels.push(addition.label);
  }
  return { ids, labels };
}

/** "Select all matching" + "Clear" row shared by the two long option lists. */
function SelectionActions({
  onSelectAll,
  selectAllLabel,
  selectAllDisabled,
  onClear,
  clearDisabled,
  selectedCount,
}: {
  onSelectAll?: () => void;
  selectAllLabel: string;
  selectAllDisabled: boolean;
  onClear: () => void;
  clearDisabled: boolean;
  selectedCount: number;
}) {
  return (
    <div className="flex items-center gap-3 text-xs">
      {onSelectAll && (
        <button
          type="button"
          onClick={onSelectAll}
          disabled={selectAllDisabled}
          className="font-medium text-slate-700 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
        >
          {selectAllLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onClear}
        disabled={clearDisabled}
        className="font-medium text-slate-700 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
      >
        Clear
      </button>
      {selectedCount > 0 && <span className="text-slate-500">{selectedCount} selected</span>}
    </div>
  );
}

function ChoiceFilterBody({
  group,
  selection,
  showApplyToAll,
  onUpdate,
}: {
  group: ReportSubFilterGroupWithChoices;
  selection: SubFilterSelection;
  showApplyToAll: boolean;
  onUpdate: (patch: Partial<SubFilterSelection>) => void;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim();
  const idsClean = selection.ids.filter((id) => id !== '' && id !== '0');

  // Appointment Type labels already read "Facility > Type", so a plain
  // substring match over the whole label lets a facility name surface every
  // type under it.
  const visibleChoices = useMemo(
    () => (term === '' ? group.choices : group.choices.filter((c) => matchesSearch(c.label, term))),
    [group.choices, term],
  );

  const toggle = (choice: { id: string; label: string }, checked: boolean) => {
    if (checked) {
      onUpdate({
        ids: [...idsClean, choice.id],
        labels: [...selection.labels, choice.label],
      });
    } else {
      onUpdate({
        ids: idsClean.filter((id) => id !== choice.id),
        labels: selection.labels.filter((label) => label !== choice.label),
      });
    }
  };

  /**
   * Select every option the search currently shows — not every option in the
   * category. Legacy's `checkAllFilters` ticked rows DataTables had hidden
   * (admin_report_list.php:3831), which silently selected far more than the
   * user could see.
   */
  const selectAllVisible = () => {
    onUpdate(mergeSelection({ ids: idsClean, labels: selection.labels }, visibleChoices));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-900">{group.label}</div>
        {showApplyToAll && group.supportsApplyToAll && (
          <label className="flex items-center gap-1 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={Boolean(selection.applyToAll)}
              onChange={(e) => onUpdate({ applyToAll: e.target.checked })}
            />
            Apply to All
          </label>
        )}
      </div>
      {group.choices.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500">
          No {group.label.toLowerCase()} available for this account.
        </div>
      ) : (
        <>
          <OptionSearchBox
            value={search}
            onChange={setSearch}
            matchCount={visibleChoices.length}
            totalCount={group.choices.length}
            label={group.label}
          />
          <SelectionActions
            onSelectAll={selectAllVisible}
            selectAllLabel={term === '' ? 'Select all' : `Select ${visibleChoices.length} matching`}
            selectAllDisabled={visibleChoices.length === 0}
            onClear={() => onUpdate({ ids: [], labels: [] })}
            clearDisabled={idsClean.length === 0}
            selectedCount={idsClean.length}
          />
          <div className="max-h-56 overflow-y-auto rounded-md border border-slate-300 bg-white p-2">
            {visibleChoices.length === 0 ? (
              <div className="px-1 py-2 text-xs text-slate-500">No matches for “{term}”.</div>
            ) : (
              visibleChoices.map((choice) => {
                const checked = idsClean.includes(choice.id);
                return (
                  <label
                    key={choice.id}
                    className="flex cursor-pointer items-center gap-2 py-0.5 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggle(choice, e.target.checked)}
                    />
                    <span className="text-slate-700">{choice.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ZipRangeFilterBody({
  group,
  selection,
  onUpdate,
}: {
  group: ReportSubFilterGroupWithChoices;
  selection: SubFilterSelection;
  onUpdate: (patch: Partial<SubFilterSelection>) => void;
}) {
  const [from, to] = selection.ids;
  // The zipRange UI control updates the composite group selection — encoder
  // emits two legacy dim entries (16 = zipCode1, 17 = zipCode2) on save.
  const update = (nextFrom: string, nextTo: string) => {
    const ids = nextFrom && nextTo ? [nextFrom, nextTo] : [];
    onUpdate({ ids, labels: ids.length ? [group.label] : [] });
  };
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-slate-900">{group.label}</div>
      <div className="flex items-center gap-3 text-sm">
        <label className="flex items-center gap-1">
          From
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            value={from ?? ''}
            onChange={(e) => update(e.target.value.replace(/[^\d]/g, ''), to ?? '')}
            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            placeholder="00000"
          />
        </label>
        <label className="flex items-center gap-1">
          To
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            value={to ?? ''}
            onChange={(e) => update(from ?? '', e.target.value.replace(/[^\d]/g, ''))}
            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            placeholder="99999"
          />
        </label>
      </div>
    </div>
  );
}

function RadioFilterBody({
  group,
  selection,
  onUpdate,
  choices,
  scalarLabel,
}: {
  group: ReportSubFilterGroupWithChoices;
  selection: SubFilterSelection;
  onUpdate: (patch: Partial<SubFilterSelection>) => void;
  choices: Array<{ id: string; label: string }>;
  scalarLabel: string;
}) {
  const current = selection.ids[0] ?? '0';
  const update = (next: string) => {
    if (next === '0' || next === '') {
      onUpdate({ ids: [], labels: [] });
    } else {
      onUpdate({ ids: [next], labels: [scalarLabel] });
    }
  };
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-slate-900">{group.label}</div>
      <div className="space-y-1 text-sm text-slate-700">
        {choices.map((c) => (
          <label key={c.id} className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name={`radio-${group.key}`}
              checked={current === c.id || (c.id === '0' && current === '')}
              onChange={() => update(c.id)}
            />
            {c.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function FeesDiscountsFilterBody({
  selection,
  onUpdate,
}: {
  selection: SubFilterSelection;
  onUpdate: (patch: Partial<SubFilterSelection>) => void;
}) {
  // Composite: Fees (dim 24) + Discounts (dim 25). We carry both in a single
  // group selection — id "F<n>" => fees=n, "D<n>" => discounts=n. The encoder
  // expands these into separate dim 24 / 25 entries in `subFilters`.
  // For now the wizard only supports an "either/or" toggle per facet matching
  // legacy radios; full encoder split happens in buildPayload. To keep the
  // wire format simple we store ids as "F1" / "D2" plus labels "Fees"/"Discounts".
  const fees = selection.ids.find((id) => id.startsWith('F')) ?? '';
  const discounts = selection.ids.find((id) => id.startsWith('D')) ?? '';

  const update = (nextFees: string, nextDiscounts: string) => {
    const ids: string[] = [];
    const labels: string[] = [];
    if (nextFees) {
      ids.push(nextFees);
      labels.push('Fees');
    }
    if (nextDiscounts) {
      ids.push(nextDiscounts);
      labels.push('Discounts');
    }
    onUpdate({ ids, labels });
  };

  const Radio = ({
    name,
    value,
    current,
    onPick,
    label,
  }: {
    name: string;
    value: string;
    current: string;
    onPick: (v: string) => void;
    label: string;
  }) => (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input type="radio" name={name} checked={current === value} onChange={() => onPick(value)} />
      {label}
    </label>
  );

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-slate-900">Fees</div>
        <div className="space-y-1">
          <Radio name="fees" value="" current={fees} onPick={(v) => update(v, discounts)} label="All Plans" />
          <Radio name="fees" value="F1" current={fees} onPick={(v) => update(v, discounts)} label="Plans w/ Fees" />
          <Radio name="fees" value="F2" current={fees} onPick={(v) => update(v, discounts)} label="Plans w/ No Fees" />
        </div>
      </div>
      <div>
        <div className="text-sm font-medium text-slate-900">Discounts</div>
        <div className="space-y-1">
          <Radio name="disc" value="" current={discounts} onPick={(v) => update(fees, v)} label="All Plans" />
          <Radio name="disc" value="D1" current={discounts} onPick={(v) => update(fees, v)} label="Plans w/ Discounts" />
          <Radio name="disc" value="D2" current={discounts} onPick={(v) => update(fees, v)} label="Plans w/ No Discounts" />
        </div>
      </div>
    </div>
  );
}

function ProcedureCountFilterBody({
  selection,
  onUpdate,
}: {
  selection: SubFilterSelection;
  onUpdate: (patch: Partial<SubFilterSelection>) => void;
}) {
  const value = selection.ids[0] ?? '';
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-slate-900">Procedures (#)</div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        Number of procedures per plan:
        <input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={value}
          onChange={(e) => {
            const next = e.target.value.replace(/[^\d]/g, '');
            if (next === '') onUpdate({ ids: [], labels: [] });
            else onUpdate({ ids: [next], labels: ['Procedures (#)'] });
          }}
          className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          placeholder="any"
        />
      </label>
    </div>
  );
}
